import React, { useEffect, useState } from "react";
import ReactCropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Particles from "react-tsparticles";
import { loadConfettiPreset } from "tsparticles-preset-confetti";
import { useNavigate } from "react-router-dom";

import {
  Trophy,
  User,
  Mail,
  Phone,
  MapPin,
  Camera,
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  RotateCcw,
  X,
  Upload,
  Loader2,
} from "lucide-react";

const api = import.meta.env.VITE_API_URL;

// Registration closing date
const TARGET_DATE = new Date("2026-10-07T00:00:00");

const UserFormXYZ = () => {
  const getTimeLeft = () => {
    const diff = TARGET_DATE - new Date();

    if (diff <= 0) return null;

    return {
      Days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      Hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      Minutes: Math.floor((diff / (1000 * 60)) % 60),
      Seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [time, setTime] = useState(getTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(getTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [submit, setSubmit] = useState("Submit Registration");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    photo: "",
    front_adhar: "",
    back_adhar: "",
    role: "1",
    style: "1",
    islocal: "1",
  });

  const [term, setTerm] = useState(false);

  const [imageData, setImageData] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [croppedImages, setCroppedImages] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [cropperInstances, setCropperInstances] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [activeField, setActiveField] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Input Change
  |--------------------------------------------------------------------------
  */

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "email" ? value.toLowerCase() : value,
    }));

    setError("");
  };

  /*
  |--------------------------------------------------------------------------
  | Image Upload
  |--------------------------------------------------------------------------
  */

  const handleImageUpload = (e, field) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // 2MB validation
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size must be less than 2MB.");
      e.target.value = "";
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setImageData((prev) => ({
        ...prev,
        [field]: reader.result,
      }));

      setActiveField(field);
      setError("");
    };

    reader.readAsDataURL(file);

    // Allow selecting the same file again
    e.target.value = "";
  };

  /*
  |--------------------------------------------------------------------------
  | Crop Image
  |--------------------------------------------------------------------------
  */

  const handleCrop = (field) => {
    const cropper = cropperInstances[field];

    if (!cropper) {
      setError("Unable to process this image. Please try again.");
      return;
    }

    const croppedCanvas = cropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    if (!croppedCanvas) {
      setError("Unable to crop image. Please try again.");
      return;
    }

    const croppedImage = croppedCanvas.toDataURL("image/jpeg", 0.9);

    setCroppedImages((prev) => ({
      ...prev,
      [field]: croppedImage,
    }));

    setFormData((prev) => ({
      ...prev,
      [field]: croppedImage,
    }));

    setActiveField(null);
    setError("");
  };

  /*
  |--------------------------------------------------------------------------
  | Re-upload
  |--------------------------------------------------------------------------
  */

  const handleReupload = (field) => {
    setImageData((prev) => ({
      ...prev,
      [field]: null,
    }));

    setCroppedImages((prev) => ({
      ...prev,
      [field]: null,
    }));

    setFormData((prev) => ({
      ...prev,
      [field]: "",
    }));

    setActiveField(null);
  };

  /*
  |--------------------------------------------------------------------------
  | Close Cropper
  |--------------------------------------------------------------------------
  */

  const closeCropper = () => {
    setActiveField(null);
  };

  /*
  |--------------------------------------------------------------------------
  | Confetti
  |--------------------------------------------------------------------------
  */

  const particlesInit = async (main) => {
    await loadConfettiPreset(main);
  };

  const particlesOptions = {
    preset: "confetti",
    fullScreen: {
      enable: true,
      zIndex: 1,
    },
  };

  /*
  |--------------------------------------------------------------------------
  | Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!term) {
      setError("Please confirm that all the details provided are correct.");
      return;
    }

    if (
      !formData.name.trim() ||
      !formData.phone.trim() ||
      !formData.email.trim() ||
      !formData.address.trim()
    ) {
      setError("Please fill in all required details.");
      return;
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (
      !croppedImages.photo ||
      !croppedImages.front_adhar ||
      !croppedImages.back_adhar
    ) {
      setError("Please upload your photo and both sides of Aadhaar.");
      return;
    }

    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      // Don't send base64 images as normal form fields
      if (
        key !== "photo" &&
        key !== "front_adhar" &&
        key !== "back_adhar"
      ) {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      setSubmit("Submitting...");
      
      /*
      |--------------------------------------------------------------------------
      | Photo
      |--------------------------------------------------------------------------
      */

      if (croppedImages.photo) {
        const blob = await fetch(croppedImages.photo).then((res) =>
          res.blob()
        );

        formDataToSend.append("photo", blob, "photo.jpg");
      }

      /*
      |--------------------------------------------------------------------------
      | Aadhaar Front
      |--------------------------------------------------------------------------
      */

      if (croppedImages.front_adhar) {
        const blob = await fetch(croppedImages.front_adhar).then((res) =>
          res.blob()
        );

        formDataToSend.append(
          "front_adhar",
          blob,
          "front_adhar.jpg"
        );
      }

      /*
      |--------------------------------------------------------------------------
      | Aadhaar Back
      |--------------------------------------------------------------------------
      */

      if (croppedImages.back_adhar) {
        const blob = await fetch(croppedImages.back_adhar).then((res) =>
          res.blob()
        );

        formDataToSend.append(
          "back_adhar",
          blob,
          "back_adhar.jpg"
        );
      }

      const response = await fetch(`${api}/api/players`, {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("player", data.id);

        setIsSubmitted(true);

        navigate(`/payment/${data.id}`);
      } else {
        console.error("Registration failed:", data);

        setSubmit("Try Again");

        setError(
          data?.message ||
            "Registration failed. Please check your details and try again."
        );
      }
    } catch (error) {
      console.error("Registration error:", error);

      setSubmit("Try Again");

      setError(
        "Unable to connect to the server. Please try again."
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Registration Closed
  |--------------------------------------------------------------------------
  */

  if (!time && !isSubmitted) {
    return (
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 py-12 text-white">

        <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-red-600/20 blur-3xl" />

        <div className="pointer-events-none absolute -right-32 bottom-20 h-80 w-80 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative z-10 w-full max-w-lg rounded-3xl border border-white/10 bg-white/[0.06] p-8 text-center shadow-2xl backdrop-blur-xl sm:p-10">

          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-yellow-400/20 bg-yellow-400/10">
            <Trophy className="h-10 w-10 text-yellow-400" />
          </div>

          <p className="mt-6 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h1 className="mt-3 text-3xl font-black sm:text-4xl">
            Registration Closed
          </h1>

          <p className="mt-4 leading-7 text-gray-400">
            Player registration for Badgaw Premier League Season 2
            is currently closed.
          </p>

          <div className="mt-6 rounded-2xl border border-yellow-400/20 bg-yellow-400/5 p-4">
            <p className="text-sm font-semibold text-yellow-300">
              Wait for the auction date
            </p>
          </div>

        </div>
      </section>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Success
  |--------------------------------------------------------------------------
  */

  if (isSubmitted) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 text-white">

        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesOptions}
        />

        <div className="relative z-10 w-full max-w-lg rounded-3xl border border-green-400/20 bg-white/[0.06] p-8 text-center shadow-2xl backdrop-blur-xl sm:p-10">

          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
            <CheckCircle2 className="h-12 w-12 text-green-400" />
          </div>

          <p className="mt-6 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h2 className="mt-3 text-3xl font-black sm:text-4xl">
            Registration Successful!
          </h2>

          <p className="mt-4 text-gray-300">
            Your player registration has been submitted successfully.
          </p>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Redirecting to your profile...
          </div>

        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Main Registration Form
  |--------------------------------------------------------------------------
  */

  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 py-8 text-white sm:px-6 md:py-12">

      {/* Background */}
      <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-red-600/20 blur-3xl" />

      <div className="pointer-events-none absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-3xl">

        {/* Header */}
        <div className="mb-8 text-center">

          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-yellow-400/20 bg-yellow-400/10">
            <Trophy className="h-8 w-8 text-yellow-400" />
          </div>

          <p className="text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h1 className="mt-2 text-3xl font-black sm:text-4xl md:text-5xl">
            Player Registration
          </h1>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-400 sm:text-base">
            Register yourself for the Badgaw Premier League Season 2.
            Fill in your details carefully before submitting.
          </p>

        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl backdrop-blur-xl"
        >

          {/* Form Header */}
          <div className="border-b border-white/10 bg-gradient-to-r from-red-700/40 to-orange-500/20 p-6 sm:p-8">

            <div className="flex items-center gap-4">

              <div className="rounded-xl bg-red-500/10 p-3">
                <User className="h-6 w-6 text-orange-400" />
              </div>

              <div>
                <h2 className="text-xl font-bold">
                  Player Details
                </h2>

                <p className="mt-1 text-sm text-gray-400">
                  Enter your personal information
                </p>
              </div>

            </div>

          </div>

          <div className="space-y-7 p-6 sm:p-8">

            {/* Personal Details */}
            <div className="grid gap-5 md:grid-cols-2">

              {/* Name */}
              <div className="md:col-span-2">

                <label className="mb-2 block text-sm font-semibold text-gray-200">
                  Full Name
                  <span className="ml-1 text-red-400">*</span>
                </label>

                <div className="relative">

                  <User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full rounded-xl border border-white/10 bg-black/30 py-3.5 pl-12 pr-4 text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />

                </div>

              </div>

              {/* Email */}
              <div>

                <label className="mb-2 block text-sm font-semibold text-gray-200">
                  Email
                  <span className="ml-1 text-red-400">*</span>
                </label>

                <div className="relative">

                  <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-white/10 bg-black/30 py-3.5 pl-12 pr-4 text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />

                </div>

              </div>

              {/* Phone */}
              <div>

                <label className="mb-2 block text-sm font-semibold text-gray-200">
                  Mobile Number
                  <span className="ml-1 text-red-400">*</span>
                </label>

                <div className="relative">

                  <Phone className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />

                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    className="w-full rounded-xl border border-white/10 bg-black/30 py-3.5 pl-12 pr-4 text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />

                </div>

              </div>

              {/* Address */}
              <div className="md:col-span-2">

                <label className="mb-2 block text-sm font-semibold text-gray-200">
                  Address
                  <span className="ml-1 text-red-400">*</span>
                </label>

                <div className="relative">

                  <MapPin className="absolute left-4 top-4 h-5 w-5 text-gray-500" />

                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your complete address"
                    rows={3}
                    className="w-full resize-none rounded-xl border border-white/10 bg-black/30 py-3.5 pl-12 pr-4 text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />

                </div>

              </div>

            </div>

            {/* Upload Section */}
            <div className="border-t border-white/10 pt-7">

              <div className="mb-5 flex items-center gap-3">

                <div className="rounded-xl bg-orange-500/10 p-3">
                  <Camera className="h-6 w-6 text-orange-400" />
                </div>

                <div>
                  <h2 className="text-xl font-bold">
                    Documents & Photo
                  </h2>

                  <p className="text-sm text-gray-400">
                    Upload clear images of the required documents
                  </p>
                </div>

              </div>

              <div className="grid gap-5 md:grid-cols-3">

                {[
                  {
                    field: "photo",
                    title: "Player Photo",
                    icon: Camera,
                  },
                  {
                    field: "front_adhar",
                    title: "Aadhaar Front",
                    icon: CreditCard,
                  },
                  {
                    field: "back_adhar",
                    title: "Aadhaar Back",
                    icon: CreditCard,
                  },
                ].map(({ field, title, icon: Icon }) => (

                  <div key={field}>

                    <label className="mb-2 block text-sm font-semibold">
                      {title}
                      <span className="ml-1 text-red-400">*</span>
                    </label>

                    {!croppedImages[field] ? (
                      <label className="group flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-gray-600 bg-black/20 p-5 text-center transition hover:border-orange-400 hover:bg-orange-500/5">

                        <Icon className="mb-3 h-8 w-8 text-gray-500 transition group-hover:text-orange-400" />

                        <span className="text-sm font-semibold text-gray-300">
                          Click to upload
                        </span>

                        <span className="mt-1 text-xs text-gray-500">
                          JPG, PNG • Max 2MB
                        </span>

                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={(e) =>
                            handleImageUpload(e, field)
                          }
                          className="hidden"
                        />

                      </label>
                    ) : (
                      <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-4">

                        <img
                          src={croppedImages[field]}
                          alt={title}
                          className={`mx-auto rounded-xl object-cover ${
                            field === "photo"
                              ? "h-28 w-28"
                              : "h-28 w-full"
                          }`}
                        />

                        <button
                          type="button"
                          onClick={() => handleReupload(field)}
                          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-white/10 py-2 text-sm font-semibold text-gray-300 transition hover:bg-white/15"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Re-upload
                        </button>

                      </div>
                    )}

                  </div>

                ))}

              </div>

            </div>

            {/* Player Type */}
            <div className="border-t border-white/10 pt-7">

              <div className="mb-5 flex items-center gap-3">

                <div className="rounded-xl bg-blue-500/10 p-3">
                  <ShieldCheck className="h-6 w-6 text-blue-400" />
                </div>

                <div>
                  <h2 className="text-xl font-bold">
                    Cricket Details
                  </h2>

                  <p className="text-sm text-gray-400">
                    Select your playing details
                  </p>
                </div>

              </div>

              <div className="grid gap-5 md:grid-cols-3">

                {/* Role */}
                <div>

                  <label className="mb-2 block text-sm font-semibold">
                    Playing Role
                  </label>

                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-white/10 bg-gray-900 px-4 py-3.5 text-white outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  >
                    <option value="1">Batsman</option>
                    <option value="2">Wicket-Keeper</option>
                    <option value="3">Batsman-Allrounder</option>
                    <option value="4">Bowler-Allrounder</option>
                    <option value="5">Bowler</option>
                  </select>

                </div>

                {/* Style */}
                <div>

                  <label className="mb-2 block text-sm font-semibold">
                    Batting Style
                  </label>

                  <select
                    name="style"
                    value={formData.style}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-white/10 bg-gray-900 px-4 py-3.5 text-white outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  >
                    <option value="1">Right-Handed</option>
                    <option value="2">Left-Handed</option>
                  </select>

                </div>

                {/* Local */}
                <div>

                  <label className="mb-2 block text-sm font-semibold">
                    Player Category
                  </label>

                  <select
                    name="islocal"
                    value={formData.islocal}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-white/10 bg-gray-900 px-4 py-3.5 text-white outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  >
                    <option value="1">Local</option>
                    <option value="0">Overseas</option>
                  </select>

                </div>

              </div>

            </div>

            {/* Terms */}
            <div className="border-t border-white/10 pt-7">

              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">

                <input
                  type="checkbox"
                  checked={term}
                  onChange={() => {
                    setTerm(!term);
                    setError("");
                  }}
                  className="mt-0.5 h-5 w-5 accent-orange-500"
                />

                <span className="text-sm leading-6 text-gray-300">
                  I confirm that all the information provided by me is
                  correct and that the uploaded documents belong to me.
                  <span className="ml-1 text-red-400">*</span>
                </span>

              </label>

            </div>

            {/* Error */}
            {error && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-sm font-medium text-red-300">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submit === "Submitting..."}
              className="group flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 py-4 text-base font-black text-white shadow-lg shadow-red-950/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submit === "Submitting..." ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Submitting Registration...
                </>
              ) : (
                <>
                  <Trophy className="h-5 w-5" />
                  {submit}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              By submitting this form, you agree to participate in
              BPL Season 2 according to the tournament rules.
            </p>

          </div>

        </form>

      </div>

      {/* Cropper Modal */}
      {activeField && imageData[activeField] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">

          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-gray-950 shadow-2xl">

            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">

              <div>
                <h3 className="font-bold text-white">
                  Crop Image
                </h3>

                <p className="text-xs text-gray-500">
                  Adjust the image before uploading
                </p>
              </div>

              <button
                type="button"
                onClick={closeCropper}
                className="rounded-lg p-2 text-gray-400 transition hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>

            </div>

            {/* Cropper */}
            <div className="bg-black p-4">

              <ReactCropper
                src={imageData[activeField]}
                style={{
                  width: "100%",
                  height: "min(65vh, 500px)",
                }}
                aspectRatio={
                  activeField === "photo"
                    ? 1
                    : NaN
                }
                viewMode={1}
                guides
                zoomable
                cropBoxResizable
                cropBoxMovable
                responsive
                background={false}
                onInitialized={(instance) =>
                  setCropperInstances((prev) => ({
                    ...prev,
                    [activeField]: instance,
                  }))
                }
              />

            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 border-t border-white/10 p-4">

              <button
                type="button"
                onClick={closeCropper}
                className="flex-1 rounded-xl border border-white/10 bg-white/5 py-3 font-semibold text-gray-300 transition hover:bg-white/10"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => handleCrop(activeField)}
                className="flex-1 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 py-3 font-bold text-white transition hover:opacity-90"
              >
                Crop & Continue
              </button>

            </div>

          </div>

        </div>
      )}

    </main>
  );
};

export default UserFormXYZ;